import type { EmailVerificationWebApp } from '@/app/store/api/restApi';
import { isTelegramWebApp } from '../../../shared/lib/tgUtils.ts';
import {
  createEmailCodeRequestStore,
  extractEmailCodeDomainError,
  parseEmailCodeSearch,
  type EmailCodeEntry,
} from '../../emailCodeVerification/lib/emailCodeVerification.ts';

export const profileEmailVerificationStore = createEmailCodeRequestStore(
  'client-profile-email-verification-request-v1',
);

export type ProfileEmailRequestFailure =
  | { kind: 'alreadySet' }
  | { kind: 'conflict' }
  | { kind: 'linkUnavailable' }
  | { kind: 'rateLimited'; retryAt?: string }
  | { kind: 'unknown' };

export type ProfileEmailVerifyFailureKind =
  | 'invalidCode'
  | 'attemptsExhausted'
  | 'requestExpired'
  | 'requestConsumed'
  | 'requestNotFound'
  | 'alreadySet'
  | 'conflict'
  | 'unknown';

export interface ProfileEmailVerifyFailure {
  kind: ProfileEmailVerifyFailureKind;
  invalidCode: boolean;
  attemptsExhausted: boolean;
  terminal: boolean;
  attemptsLeft?: number;
}

export const classifyProfileEmailRequestError = (error: unknown): ProfileEmailRequestFailure => {
  const domainError = extractEmailCodeDomainError(error);
  switch (domainError.code) {
    case 'VERIFIED_EMAIL_ALREADY_SET':
      return { kind: 'alreadySet' };
    case 'VERIFIED_EMAIL_CONFLICT':
      return { kind: 'conflict' };
    case 'EMAIL_VERIFICATION_LINK_UNAVAILABLE':
      return { kind: 'linkUnavailable' };
    case 'EMAIL_VERIFICATION_RATE_LIMITED':
      return { kind: 'rateLimited', retryAt: domainError.details?.retryAt };
    default:
      return { kind: 'unknown' };
  }
};

export const classifyProfileEmailVerifyError = (error: unknown): ProfileEmailVerifyFailure => {
  const domainError = extractEmailCodeDomainError(error);
  const attemptsLeft = domainError.details?.attemptsLeft;
  const attemptsExhausted =
    domainError.code === 'EMAIL_VERIFICATION_ATTEMPTS_EXHAUSTED' ||
    (domainError.code === 'EMAIL_VERIFICATION_INVALID_CODE' && attemptsLeft === 0);
  const kind: ProfileEmailVerifyFailureKind = attemptsExhausted
    ? 'attemptsExhausted'
    : domainError.code === 'EMAIL_VERIFICATION_INVALID_CODE'
      ? 'invalidCode'
      : domainError.code === 'EMAIL_VERIFICATION_REQUEST_EXPIRED'
        ? 'requestExpired'
        : domainError.code === 'EMAIL_VERIFICATION_REQUEST_CONSUMED'
          ? 'requestConsumed'
          : domainError.code === 'EMAIL_VERIFICATION_REQUEST_NOT_FOUND'
            ? 'requestNotFound'
            : domainError.code === 'VERIFIED_EMAIL_ALREADY_SET'
              ? 'alreadySet'
              : domainError.code === 'VERIFIED_EMAIL_CONFLICT'
                ? 'conflict'
                : 'unknown';

  return {
    kind,
    invalidCode: domainError.code === 'EMAIL_VERIFICATION_INVALID_CODE',
    attemptsExhausted,
    terminal:
      attemptsExhausted ||
      kind === 'requestExpired' ||
      kind === 'requestConsumed' ||
      kind === 'requestNotFound' ||
      kind === 'alreadySet' ||
      kind === 'conflict',
    attemptsLeft,
  };
};

export const getEmailVerificationWebApp = (): EmailVerificationWebApp =>
  (isTelegramWebApp() ? 'telegram' : 'web') as EmailVerificationWebApp;

export const hasProfileEmailVerificationCompletedExternally = (
  initialVerifiedEmail: string | undefined,
  verifiedEmail: string | undefined,
): boolean => !initialVerifiedEmail && Boolean(verifiedEmail);

export const parseTelegramEmailVerificationEntry = (value: string | null): EmailCodeEntry | null => {
  const prefix = 'emailVerify_';
  if (!value?.startsWith(prefix)) return null;

  const separatorIndex = value.lastIndexOf('_');
  const codeId = value.slice(prefix.length, separatorIndex < prefix.length ? undefined : separatorIndex);
  const code = separatorIndex < prefix.length ? '' : value.slice(separatorIndex + 1);
  const params = new URLSearchParams({ codeId, code });
  return parseEmailCodeSearch(`?${params.toString()}`);
};

export const removeLaunchParams = (value: string): string => {
  const url = new URL(value, 'https://client.invalid');
  url.searchParams.delete('tgWebAppStartParam');
  url.searchParams.delete('startapp');
  return `${url.pathname}${url.search}${url.hash}`;
};
